import type { SyntheticEvent } from 'react';
import type { ProjectType } from '@atlassian/jira-common-constants';
import type { UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import type { AccessLevel } from '@atlassian/jira-shared-types';
import type {
	AccessTokenModalType,
	ACCESS_TOKEN_EXPIRING,
	ACCESS_TOKEN_NOT_FOUND,
	ACCESS_TOKEN_VALID,
} from '../constants/access-token';
import type { ValidationErrorType } from '../constants/errors';
import type {
	JiraFieldType,
	ThirdPartyField,
	CustomFieldType,
	Field,
	IssueLink,
	FieldId,
	ThirdPartyFieldId,
} from '../constants/fields';
import type { ImportType, SupportedThirdParty } from '../constants/import-type';
import type { ProjectStyle, TemplateType } from '../constants/project';
import type { Source } from '../constants/source';
import type { SourceItem } from '../constants/source-items';
import type { Step } from '../constants/steps';

export type MigrationId = string;

export type ColumnName = string;

export type UploadedFileName = string | null;

export type ProjectKey = string | null;

export type ProjectName = string | null;

export type RowValue = string | null;
export type RowData = {
	[columnName: ColumnName]: RowValue;
};

export type Column = {
	columnName: ColumnName;
	field?: Field | null;
};

export type IssueLinkColumn = {
	columnName: ColumnName;
	issueLink: IssueLink;
};

export type ColumnState = {
	isVisible: boolean;
	isIncluded?: boolean;
} & Column;

export type ColumnMap = { [name: ColumnName]: ColumnState };

export type ColumnErrors = {
	[columnName: ColumnName]: ValidationErrorType;
};

export type X2CImportValidationError = {
	columnErrors: ColumnErrors;
	missingFields: FieldId[];
};

export type CSVUserEmail = {
	userKey: string;
	userValue?: string | null;
	cellValue: string;
};

export type UserMappingMap = { [userKey: string]: CSVUserEmail };

export type MappingState = {
	columnOrder: ColumnName[];
	columns: ColumnMap;
	rows: RowData[];
	availableJiraFields: Field[];
	availableIssueLinkFields: Field[];
	availableNewFieldTypes: CustomFieldType[];
	mappedJiraFieldIds: Set<FieldId>;
	validationError: X2CImportValidationError | null;
	selectedColumn: ColumnName | undefined;
	userMapping: UserMappingMap | null;
	shouldImportUsers: boolean;
	issueCount: string | null;
	errorsFilter: ColumnName[] | null;
	preMappedFieldCount: number;
	mappedLinkFieldsCount: number;
	preFilledUsersCount: number;
};

export type SelectOption = {
	label: string;
	value: string;
};

export type DateProperties = {
	dateFormat: string;
	timeZone: string;
	locale: string;
};

export type AdvancedOptions = {
	dateFormat: string;
	fileEncoding: string;
	delimiter: string;
	timeZoneOption: SelectOption;
	localeOption: SelectOption;
};

export type ProjectData = {
	name: string;
	key: string;
	type: ProjectType | null;
	style: ProjectStyle | null;
	accessLevel?: AccessLevel | null;
	template?: TemplateType | null;
};

export const CSV_FLOW = 'CSV';
export const API_FLOW = 'API';
export type ImporterFlow = typeof CSV_FLOW | typeof API_FLOW;

export type CreateProjectConfig = {
	isRecommended: boolean;
	productType: ProjectType;
	projectStyles: ProjectStyle[];
};

export type ImporterSourceItem = SourceItem & {
	supportedFlow: ImporterFlow;
	[CSV_FLOW]: CreateProjectConfig[];
	[API_FLOW]: CreateProjectConfig[];
};

export type State = {
	sourceItems: ImporterSourceItem[] | null;
	mappingState: MappingState | null;
	migrationId: MigrationId | null;
	step: Step;
	importType: ImportType;
	uploadedFileName: UploadedFileName;
	advancedOptions: AdvancedOptions;
	projectData: ProjectData;
	thirdPartyState: ThirdPartyState;
	source: Source;
};

export type BaseTable = {
	scrollToLeft(scrollLeft: number): void;
	table: {
		props: {
			width: number;
		};
		bodyRef: {
			state: {
				scrollLeft: number;
			};
		};
	};
};
export type TableRefCurrent = BaseTable | null;

export type Priority = {
	self: string;
	statusColor: string;
	description: string;
	iconUrl: string;
	name: string;
	id: string;
	isDefault: boolean;
};

export type PriorityMap = Map<string, Priority>;

export type PrioritiesResponse = Promise<{
	isLast: boolean;
	maxResults: number;
	startAt: number;
	values: Priority[];
}>;

export type PriorityData = PriorityMap | undefined;

export type PrioritiesState = {
	data: PriorityData;
	isLoading: boolean;
	error: Error | null;
};

export type ThirdPartyEntity = {
	id: string;
	name: string;
	iconUrl?: string;
	isStarred?: boolean;
	style?: ProjectStyle;
};

export type ThirdPartySubfolders = {
	[folderId: string]: ThirdPartyEntity[];
};

export type ThirdPartyFolderAndSubfolders = {
	folders: ThirdPartyEntity[];
	subfolders: ThirdPartySubfolders;
};

export type ThirdPartyEntityPage = {
	entities: ThirdPartyEntity[];
	offset: string | null;
};

export type ThirdPartyEntityStats = {
	members?: number;
	tasks?: number;
	tasksCount?: string;
};

export type ThirdPartyMembers = {
	id: string;
	username: string;
	fullName: string;
	email?: string;
};

export type JiraStatus = {
	name: string;
	statusCategory: string;
};

export type ThirdPartyStatus = {
	sourceStatusName: string;
	mappedStatusName: string;
	statusCategory: string;
};

export type ThirdPartyEntityStatusMappings = {
	entityId: string;
	entityName: string;
	statusMappings: ThirdPartyStatus[];
};

export type ThirdPartyEntityStatuses = {
	availableJiraStatuses: JiraStatus[];
	statusOptions: JiraStatus[];
	entityStatusMappings: ThirdPartyEntityStatusMappings[];
};

export type ThirdPartyStatusState = {
	[sourceStatusName: string]: {
		mappedStatusName: string;
		statusCategory: string;
	};
};

export type ThirdPartyEntityStatusMappingState = {
	[entityName: string]: {
		entityId: string;
		statusMappings: ThirdPartyStatusState;
	};
};

export type ThirdPartyEntityStatusesState = {
	availableJiraStatuses: JiraStatus[];
	statusOptions: JiraStatus[];
	entityStatusMappings: ThirdPartyEntityStatusMappingState;
};

export type ImportFieldMappings = {
	[thirdPartyFieldId: ThirdPartyFieldId]: Field;
};

export type ItemsConfig = {
	[itemTypeKey: string]: {
		itemTypeId?: string;
		fieldMappings: ImportFieldMappings;
		statusMappings?: ThirdPartyStatusState;
	};
};

export type StartThirdPartyImportPayload = {
	projectData: ProjectData;
	jiraUrl: string;
	userEmailMappings: UserEmails;
	fieldMappings?: ImportFieldMappings;
	itemsConfig?: ItemsConfig;
	importSettings?: ThirdPartyImportSettings;
};

export type UserEmails = {
	[id: string]: {
		emailId: string;
	};
};

export type ThirdPartyState = {
	workspaces: ThirdPartyEntity[] | null;
	spaces: ThirdPartyEntity[] | null;
	folders: ThirdPartyEntity[] | null;
	subfolders: ThirdPartySubfolders | null;
	entities: ThirdPartyEntity[] | null;
	entityStats: ThirdPartyEntityStats | null;
	selectedWorkspaceId: string | null;
	selectedSpaceId: string | null;
	selectedSpaceName: string | null;
	selectedFolderId: string | null;
	selectedSubfolderId: string | null;
	selectedEntityId: string | null;
	selectedEntities: ThirdPartyEntity[] | null;
	siteName: string | null;
	members: ThirdPartyMembers[] | null;
	userEmails: UserEmails;
	thirdPartyFieldConfigState: ThirdPartyFieldConfigState | null;
	thirdPartyImportSettings: ThirdPartyImportSettings;
	thirdPartyStatusMapping: ThirdPartyEntityStatusesState | null;
};

export type ThirdPartyItemTypeFieldMapping = {
	thirdPartyFieldOrder: ThirdPartyFieldId[];
	thirdPartyFieldMappings: ThirdPartyFieldMappingsState;
	mappedJiraFields: MappedJiraFields;
	includedJiraFieldIds: Set<FieldId>;
	itemTypeId?: string;
};

export type ThirdPartyFieldConfigStateForItem = ThirdPartyItemTypeFieldMapping & {
	availableJiraFields: AvailableJiraFields;
};

export type ThirdPartyFieldConfigState = {
	fieldMappingState: {
		[itemType: string]: ThirdPartyItemTypeFieldMapping;
	};
	availableJiraFields: AvailableJiraFields;
	hierarchyMapping?: ThirdPartyHierarchyMapping;
	hasUnsupportedFieldFiltered?: boolean;
};

export type AvailableJiraFields = { [fieldType: JiraFieldType]: Field[] };

export type MappedJiraFields = { [jiraFieldId: FieldId]: ThirdPartyFieldId };
export type ThirdPartyFieldMappingsState = {
	[fieldId: ThirdPartyFieldId]: ThirdPartyFieldMapping;
};

export type ThirdPartyMappingContext = {
	isFieldDisabled: boolean;
	isIncluded: boolean;
	isRequired: boolean;
	isToggleDisabled: boolean;
};

export interface ThirdPartyFieldMapping<TPF extends Partial<ThirdPartyField> = ThirdPartyField> {
	mappingContext: ThirdPartyMappingContext;
	exampleValue?: string;
	thirdPartyField: TPF;
	mappedJiraField: Field;
	defaultMappedJiraField?: Field;
	compatibleNewFieldTypes: CustomFieldType[];
	compatibleExistingFieldTypes: JiraFieldType[];
}

export type ThirdPartyHierarchyMapping = {
	itemType: string | null;
	mappingFieldId: string;
};

export type ThirdPartyImportSettings = {
	shouldImportAttachments: boolean;
	shouldImportUsers: boolean;
	shouldImportComments: boolean;
};

export type CancelModalState = {
	isCancelImportModalOpen: boolean;
	shouldCloseDrawerOnCancel: boolean;
	onCloseModalCallback?: () => void;
	offsetModalY?: number;
};

export type AccessTokenModalState = {
	isModalOpen: boolean;
	modalType: AccessTokenModalType;
	thirdParty?: SupportedThirdParty;
	onFinishAccessTokenValidation?: (isValid: boolean) => void;
	shouldCloseAndReturnToUploadStep?: boolean;
};

export type UIState = {
	hasUnsavedFieldMappingChanges: boolean;
	hasUnsavedHierarchyMappingChanges: boolean;
	hasUnsavedStatusMappingChanges: boolean;
	cancelModal: CancelModalState;
	accessTokenModal: AccessTokenModalState;
};

export type AccessTokenStatus =
	| typeof ACCESS_TOKEN_VALID
	| typeof ACCESS_TOKEN_EXPIRING
	| typeof ACCESS_TOKEN_NOT_FOUND;

export type RequestAccessTokenMethod = (props: {
	thirdParty: SupportedThirdParty;
	modalType: AccessTokenModalType;
	shouldCloseAndReturnToUploadStep?: boolean;
}) => Promise<boolean>;

export type OnDrawerClose = (
	e: SyntheticEvent<HTMLElement>,
	analyticEvent: UIAnalyticsEvent,
) => void;
